import * as React from 'react';
import { getClosestCoordinateByStep } from '../datasheet.utils';
import { IDatasheetEditorElement } from '../datasheet.types';
import { IDatasheetComponentDto } from '@common/dto/datasheet/datasheet-component';

type Coordinates = { y: number; x: number };

type DragStart = {
  startX: number;
  startY: number;
  offsetX: number;
  offsetY: number;
};

export type DatasheetElementControllerProps<T extends IDatasheetComponentDto = IDatasheetComponentDto> = IDatasheetEditorElement<T> & {
  minWidth: number;
  minHeight: number;
};

export enum DatasheetElementResizeDirection {
  se = 'se',
  sw = 'sw',
  ne = 'ne',
  nw = 'nw',
  n = 'n',
  s = 's',
  w = 'w',
  e = 'e',
}

export const useDatasheetElementController = <T extends IDatasheetComponentDto = IDatasheetComponentDto>(props: DatasheetElementControllerProps<T>) => {
  const { 
    containerRef, 
    minHeight,
    minWidth,
    isSelected: isSelectedOnDatasheet, 
    datasheetElement,
    onSelect,
    onCoordinatesChange,
    onResize,
    datasheetGridSettings,
    resizeHandlerStrategy,
    isTabOrderSettingActive,
  } = props;

  const [headingSize, setHeadingSize] = React.useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const [coordinates, setCoordinates] = React.useState<Coordinates>({ x: datasheetElement.coordinates.x, y: datasheetElement.coordinates.y });
  const [isDragging, setIsDragging] = React.useState<boolean>(false);
  const [dragStart, setDragStart] = React.useState<DragStart>({ startX: 0, startY: 0, offsetX: 0, offsetY: 0 });
  const [size, setSize] = React.useState({ width: datasheetElement.width, height: datasheetElement.height });
  const [resizing, setResizing] = React.useState<{ active: boolean; direction?: DatasheetElementResizeDirection }>({ active: false, direction: DatasheetElementResizeDirection.e });

  const movableElementRef = React.useRef<HTMLDivElement>(null);
  const scrollContainerRef = React.useRef<HTMLElement | null>(null);
  const autoScrollFrameRef = React.useRef<number | null>(null);
  const autoScrollDirectionRef = React.useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const lastPointerPositionRef = React.useRef<{ x: number; y: number } | null>(null);

  const horizontalGridStep = datasheetGridSettings.snapToGrid ? datasheetGridSettings.horizontalGrid : 1;
  const verticalGridStep = datasheetGridSettings.snapToGrid ? datasheetGridSettings.verticalGrid : 1;

  React.useEffect(() => {
    if (datasheetElement.coordinates.x !== coordinates.x || datasheetElement.coordinates.y !== coordinates.y) {
      setCoordinates({ x: datasheetElement.coordinates.x, y: datasheetElement.coordinates.y });
    }
  }, [datasheetElement.coordinates.x, datasheetElement.coordinates.y]);

  React.useEffect(() => {
    if (datasheetElement.width !== size.width || datasheetElement.height !== size.height) {
      setSize({ width: datasheetElement.width, height: datasheetElement.height });
    }
  }, [datasheetElement.width, datasheetElement.height]);

  const stopAutoScroll = React.useCallback(() => {
    if (autoScrollFrameRef.current !== null) {
      cancelAnimationFrame(autoScrollFrameRef.current);
      autoScrollFrameRef.current = null;
    }
    autoScrollDirectionRef.current = { x: 0, y: 0 };
  }, []);

  const updateCoordinatesFromPointer = React.useCallback((clientX: number, clientY: number) => {
    const scrollContainer = scrollContainerRef.current ?? containerRef.current;

    if (!scrollContainer) {
      return false;
    }

    const containerRect = scrollContainer.getBoundingClientRect();
    const scrollLeft = scrollContainer.scrollLeft;
    const scrollTop = scrollContainer.scrollTop;

    const rawX = clientX - containerRect.left + scrollLeft - dragStart.offsetX;
    const rawY = clientY - containerRect.top + scrollTop - dragStart.offsetY;

    const snappedX = getClosestCoordinateByStep(
      Math.max(horizontalGridStep, rawX),
      horizontalGridStep
    );
    const snappedY = getClosestCoordinateByStep(
      Math.max(verticalGridStep, rawY),
      verticalGridStep
    );

    const coordinatesChanged = snappedX !== coordinates.x || snappedY !== coordinates.y;

    if (coordinatesChanged) {
      const nextCoordinates = { x: snappedX, y: snappedY };
      setCoordinates(nextCoordinates);

      if (resizeHandlerStrategy === 'immediate') {
        onCoordinatesChange(datasheetElement.id, { coordinates: nextCoordinates });
      }
    }

    return coordinatesChanged;
  }, [containerRef, coordinates.x, coordinates.y, datasheetElement.id, dragStart.offsetX, dragStart.offsetY, horizontalGridStep, verticalGridStep, onCoordinatesChange, resizeHandlerStrategy]);

  const performAutoScroll = React.useCallback(() => {
    const scrollContainer = scrollContainerRef.current;

    if (!scrollContainer) {
      stopAutoScroll();
      return;
    }

    const { x, y } = autoScrollDirectionRef.current;

    if (x === 0 && y === 0) {
      stopAutoScroll();
      return;
    }

    // Move only a small section per trigger to avoid continuous runaway scroll
    const maxStep = 32;
    const stepX = Math.max(-maxStep, Math.min(maxStep, x));
    const stepY = Math.max(-maxStep, Math.min(maxStep, y));

    const previousScrollLeft = scrollContainer.scrollLeft;
    const previousScrollTop = scrollContainer.scrollTop;

    scrollContainer.scrollLeft += stepX;
    scrollContainer.scrollTop += stepY;

    const scrollDeltaX = scrollContainer.scrollLeft - previousScrollLeft;
    const scrollDeltaY = scrollContainer.scrollTop - previousScrollTop;

    if (!scrollDeltaX && !scrollDeltaY) {
      stopAutoScroll();
      return;
    }

    const lastPointer = lastPointerPositionRef.current;

    if (lastPointer) {
      updateCoordinatesFromPointer(lastPointer.x, lastPointer.y);
    }

    // keep scrolling while direction remains set
    autoScrollFrameRef.current = window.requestAnimationFrame(performAutoScroll);
  }, [stopAutoScroll, updateCoordinatesFromPointer]);

  const updateAutoScrollDirection = React.useCallback((e: MouseEvent) => {
    const scrollContainer = scrollContainerRef.current;

    if (!scrollContainer) {
      return;
    }

    const rect = scrollContainer.getBoundingClientRect();
    const edgeThreshold = 64;
    const minSpeed = 4;
    const maxSpeed = 16;

    const computeSpeed = (distanceFromEdge: number) => {
      const proximity = Math.max(0, edgeThreshold - distanceFromEdge);

      if (proximity <= 0) {
        return 0;
      }

      const normalized = proximity / edgeThreshold;

      return minSpeed + Math.round(normalized * (maxSpeed - minSpeed));
    };

    const distances = {
      top: e.clientY - rect.top,
      bottom: rect.bottom - e.clientY,
      left: e.clientX - rect.left,
      right: rect.right - e.clientX,
    };

    const nextDirection = { x: 0, y: 0 };

    if (distances.top < edgeThreshold) {
      nextDirection.y = -computeSpeed(distances.top);
    } else if (distances.bottom < edgeThreshold) {
      nextDirection.y = computeSpeed(distances.bottom);
    }

    if (distances.left < edgeThreshold) {
      nextDirection.x = -computeSpeed(distances.left);
    } else if (distances.right < edgeThreshold) {
      nextDirection.x = computeSpeed(distances.right);
    }

    autoScrollDirectionRef.current = nextDirection;

    if (nextDirection.x !== 0 || nextDirection.y !== 0) {
      if (autoScrollFrameRef.current === null) {
        autoScrollFrameRef.current = window.requestAnimationFrame(performAutoScroll);
      }
    } else {
      stopAutoScroll();
    }
  }, [performAutoScroll, stopAutoScroll]);

  const startDrag = React.useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const callToPreventShowingSelectionBox = () => {
      e.stopPropagation();
    };

    callToPreventShowingSelectionBox();

    const scrollContainer =
      containerRef.current?.parentElement ?? containerRef.current ?? null;

    scrollContainerRef.current = scrollContainer;

    const containerRect = scrollContainer?.getBoundingClientRect();
    const scrollLeft = scrollContainer?.scrollLeft || 0;
    const scrollTop = scrollContainer?.scrollTop || 0;

    setIsDragging(true);
    setDragStart({
      startX: e.clientX,
      startY: e.clientY,
      offsetX: containerRect ? e.clientX - (containerRect.left + coordinates.x - scrollLeft) : 0,
      offsetY: containerRect ? e.clientY - (containerRect.top + coordinates.y - scrollTop) : 0,
    });
    lastPointerPositionRef.current = { x: e.clientX, y: e.clientY };
  }, [coordinates.x, coordinates.y, containerRef]);

  const handleDragging = React.useCallback((e: MouseEvent) => {
    const datasheetContainer = containerRef.current;
    
    const callToPreventShowingSelectionBox = () => {
      e.stopPropagation();
    };

    callToPreventShowingSelectionBox();

    if (!isDragging || !datasheetContainer) {
      return;
    }

    updateAutoScrollDirection(e);
    lastPointerPositionRef.current = { x: e.clientX, y: e.clientY };

    updateCoordinatesFromPointer(e.clientX, e.clientY);
  }, [containerRef, isDragging, updateAutoScrollDirection, updateCoordinatesFromPointer]);

  const resetDragging = (e: MouseEvent) => {
    const callToPreventShowingSelectionBox = () => {
      e.stopPropagation();
    };

    callToPreventShowingSelectionBox();
    stopAutoScroll();
    setIsDragging(false);

    const coordinatesChanged = datasheetElement.coordinates.x !== coordinates.x || datasheetElement.coordinates.y !== coordinates.y;

    if (coordinatesChanged) {
      onCoordinatesChange(datasheetElement.id, { coordinates });
    }
  };

  React.useEffect(() => {
    if (isDragging && !isTabOrderSettingActive) {
      containerRef.current?.addEventListener('mousemove', handleDragging);
      containerRef.current?.addEventListener('mouseup', resetDragging);
    }

    return () => {
      containerRef.current?.removeEventListener('mousemove', handleDragging);
      containerRef.current?.removeEventListener('mouseup', resetDragging);
    };
  }, [isDragging, handleDragging, resetDragging, isTabOrderSettingActive]);

  React.useEffect(() => {
    return () => {
      stopAutoScroll();
    };
  }, [stopAutoScroll]);

  const handleResize = (e: MouseEvent) => {
    const datasheetContainer = containerRef.current;

    if (resizing.active && movableElementRef.current && datasheetContainer) {
      const datasheetContainerRect = datasheetContainer.getBoundingClientRect();
      const datasheetElementRect = movableElementRef.current.getBoundingClientRect();
      const newSize = { ...size };

      if (resizing.direction?.includes('e')) {
        newSize.width = e.clientX - datasheetElementRect.x;
      }

      if (resizing.direction?.includes('s')) {
        newSize.height = e.clientY - datasheetElementRect.y;
      }

      if (resizing.direction?.includes('w')) {
        const minX = datasheetContainerRect.x + horizontalGridStep;
        newSize.width = datasheetElementRect.right - Math.max(e.clientX, minX);

        setCoordinates((prev) => ({ 
          ...prev, 
          x: minX > e.clientX
            ? horizontalGridStep 
            : e.clientX - datasheetContainerRect.left
        }));
      }

      if (resizing.direction?.includes('n')) {
        const minY = datasheetContainerRect.y + verticalGridStep;
        newSize.height = datasheetElementRect.bottom - Math.max(e.clientY, minY);
        
        setCoordinates((prev) => ({ 
          ...prev, 
          y: minY > e.clientY
            ? verticalGridStep 
            : e.clientY - datasheetContainerRect.y 
        }));
      }

      const finalSize = {
        width: Math.max(minWidth, newSize.width),
        height: Math.max(minHeight, newSize.height),
      };

      setSize(finalSize);
      onResize(
        datasheetElement.id, 
        {
          width: finalSize.width,
          height: finalSize.height,
        }
      );
    }
  };

  const resetResizing = (e: MouseEvent) => {
    const callToPreventShowingSelectionBox = () => {
      e.stopPropagation();
    };

    callToPreventShowingSelectionBox();
    setResizing({ active: false, direction: undefined });
    onResize(
      datasheetElement.id, 
      {
        width: size.width,
        height: size.height,
        coordinates,
      }
    );
  };

  React.useEffect(() => {
    if (resizing.active && !isTabOrderSettingActive) {
      containerRef.current?.addEventListener('mousemove', handleResize);
      containerRef.current?.addEventListener('mouseup', resetResizing);
    }

    return () => {
      containerRef.current?.removeEventListener('mousemove', handleResize);
      containerRef.current?.removeEventListener('mouseup', resetResizing);
    };
  }, [resizing, coordinates, size, isTabOrderSettingActive]);

  const handleResizing = React.useCallback((e: React.MouseEvent<HTMLDivElement>, direction: DatasheetElementResizeDirection) => {
    e.stopPropagation();
    setResizing({ active: true, direction });
  }, []);

  const datasheetElementSelected = React.useMemo(() => {
    return isSelectedOnDatasheet(datasheetElement);
  }, [isSelectedOnDatasheet, datasheetElement]);

  const handleDatasheetElementMouseDown = React.useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!datasheetElementSelected) {
      onSelect(datasheetElement, e);
    }

    startDrag(e);
  }, [onSelect, datasheetElement, startDrag, datasheetElementSelected]);

  const handleHeadingSizeChange = React.useCallback((size: { width: number; height: number }) => {
    setHeadingSize(size);
  }, []);

  return {
    handleDatasheetElementMouseDown,
    datasheetElementSelected,
    coordinates,
    movableElementRef,
    handleResizing,
    size,
    handleHeadingSizeChange,
    headingSize,
  };
};